import { parse, parseISO, isValid } from 'date-fns';

/**
 * Tries to parse the date string in multiple formats.
 * Returns null if the date string is null/undefined or empty.
 * Throws an error only if the date string is present but in an unrecognized format.
 * Why we need this function?
 * I noticed that the date string returned by the API is not consistent. It varies depending on whether or not I'm using dev or prod.
 * Ideally, the API should return the date in a consistent format. I couldn't figure out why it's not consistent.
 * So, I wrote this function to handle the inconsistency.
 * @param dateString
 * @returns
 */
export function customDateParserForGuard(dateString: string | null | undefined): Date | null {
	// Handle null/undefined/empty dates - patrol may not have started yet
	if (!dateString) {
		return null;
	}

	let date;

	// Try ISO 8601 format first (handles "2024-01-23T19:45:00", "2024-01-23T19:45:00.000Z", etc.)
	date = parseISO(dateString);
	if (isValid(date)) {
		return date;
	}

	// Try 24-hour format without AM/PM (YYYY-MM-DD HH:mm:ss)
	date = parse(dateString, 'yyyy-MM-dd HH:mm:ss', new Date());
	if (isValid(date)) {
		return date;
	}

	// Try 12-hour format with AM/PM (YYYY-MM-DD hh:mm:ss aa)
	date = parse(dateString, 'yyyy-MM-dd hh:mm:ss aa', new Date());
	if (isValid(date)) {
		return date;
	}

	// Try US format with 12-hour (M/d/yyyy h:mm:ss a)
	date = parse(dateString, 'M/d/yyyy h:mm:ss a', new Date());
	if (isValid(date)) {
		return date;
	}

	// Try US format with 24-hour (M/d/yyyy HH:mm:ss)
	date = parse(dateString, 'M/d/yyyy HH:mm:ss', new Date());
	if (isValid(date)) {
		return date;
	}

	throw new Error('API returned invalid date format');
}
